<template>
  <v-app :dark="isDarkMode" :class="{ 'dark-mode': isDarkMode }">
    <!-- Header -->
    <header class="autosyncx-header">
      <div class="header-nav">
        <div class="header-nav-content">

          <!-- Mobile: Hamburger -->
          <div class="hamburger-menu" @click="toggleMobileMenu">
            <v-icon class="hamburger-icon material-icons-round">{{ mobileMenuOpen ? 'close' : 'menu' }}</v-icon>
          </div>

          <!-- Desktop: Nav Links -->
          <nav class="nav-tabs">
            <div class="nav-tab" @click="$router.push('/')">HOME</div>

            <v-menu offset-y open-on-click content-class="cars-dropdown-menu">
              <template v-slot:activator="{ on, attrs }">
                <div class="nav-tab nav-tab-dropdown" v-bind="attrs" v-on="on">
                  &#9660;&nbsp;CARS FOR SALE
                </div>
              </template>
              <v-list dense class="cars-dropdown-list">
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>All Vehicles</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Available</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Pending</v-list-item-title>
                </v-list-item>
                <v-list-item @click="$router.push('/')">
                  <v-list-item-title>Sold</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <!-- <div class="nav-tab" @click="$router.push('/reviews')">TESTIMONIALS</div> -->
            <!-- <div class="nav-tab">FINANCE</div> -->
            <div class="nav-tab" @click="$router.push('/about-us')">ABOUT US</div>
            <!-- <div class="nav-tab">CONTACT</div> -->
          </nav>

          <!-- Right: Theme Toggle -->
          <div class="nav-right">
            <div class="theme-toggle">
              <v-btn icon small @click="toggleDarkMode" class="theme-toggle-btn">
                <v-icon small class="material-icons-round">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Mobile Menu Dropdown -->
        <v-expand-transition>
          <div v-if="mobileMenuOpen" class="mobile-menu-dropdown">
            <div class="mobile-menu-item" @click="$router.push('/'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">home</i>
              <span>Home</span>
            </div>
            <div class="mobile-menu-item" @click="$router.push('/'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">directions_car</i>
              <span>Cars For Sale</span>
            </div>
            <div class="mobile-menu-item" @click="$router.push('/reviews'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">star</i>
              <span>Testimonials</span>
            </div>
            <div class="mobile-menu-item" @click="$router.push('/about-us'); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">info</i>
              <span>About Us</span>
            </div>
            <div class="mobile-menu-item" @click="toggleDarkMode">
              <i class="material-icons-round menu-item-icon">{{ isDarkMode ? 'light_mode' : 'dark_mode' }}</i>
              <span>{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
            </div>
            <div class="mobile-menu-item" @click="callDealer(); mobileMenuOpen = false">
              <i class="material-icons-round menu-item-icon">call</i>
              <span>Call Us</span>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </header>

    <!-- Loading Overlay -->
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Main Content -->
    <v-main style="padding:0px!important;margin-top:64px;">
      <div v-if="vehicle" class="vehicle-detail-container" :class="{ 'dark-mode': isDarkMode }">

        <!-- Breadcrumb Section -->
        <section class="breadcrumb-section">
          <v-container class="py-3">
            <v-breadcrumbs :items="breadcrumbItems" class="pa-0">
              <template v-slot:item="{ item }">
                <v-breadcrumbs-item
                  :href="item.href"
                  :disabled="item.disabled"
                  class="breadcrumb-item"
                >
                  {{ item.text }}
                </v-breadcrumbs-item>
              </template>
              <template v-slot:divider>
                <v-icon class="material-icons-round">chevron_right</v-icon>
              </template>
            </v-breadcrumbs>
          </v-container>
        </section>

        <!-- Main Vehicle Content -->
        <section class="vehicle-content-section">
          <v-container>
            <v-row>
              <!-- Left Column - Images and Details -->
              <v-col cols="12" lg="8">

                <!-- Vehicle Title -->
                <div class="vehicle-title-section mb-4">
                  <h1 class="vehicle-title">{{ getVehicleTitle() }}</h1>
                  <div class="vehicle-subtitle">
                    <span v-if="vehicle.odometer" class="subtitle-item">{{ formatMileage(vehicle.odometer) }}</span>
                    <span v-if="vehicle.exterior_color" class="subtitle-item">{{ vehicle.exterior_color }}</span>
                    <span v-if="vehicle.vin" class="subtitle-item">VIN: {{ vehicle.vin.slice(-6) }}</span>
                  </div>
                </div>

                <!-- Image Gallery -->
                <div class="image-gallery-section mb-6">
                  <v-card class="image-gallery-card" flat>
                    <v-carousel
                      v-if="vehicle.images && vehicle.images.length > 0"
                      height="450"
                      show-arrows-on-hover
                      hide-delimiters
                      class="vehicle-carousel"
                    >
                      <v-carousel-item
                        v-for="(image, i) in vehicle.images"
                        :key="i"
                      >
                        <v-img
                          :src="getImageSrc(image.image)"
                          height="450"
                          cover
                          @error="handleImageError"
                          class="vehicle-image"
                        >
                          <template v-slot:placeholder>
                            <v-img
                              :src="fallbackImage"
                              height="450"
                              cover
                              class="vehicle-image"
                            ></v-img>
                          </template>
                        </v-img>
                      </v-carousel-item>
                    </v-carousel>

                    <!-- Fallback single image -->
                    <v-img
                      v-else
                      :src="fallbackImage"
                      height="450"
                      cover
                      class="vehicle-image"
                    ></v-img>
                  </v-card>

                  <!-- Interested Button -->
                  <div class="interested-button-section mt-4">
                    <v-btn
                      block
                      large
                      :loading="expressingInterest"
                      class="interested-btn-main"
                      :class="{ 'dark-mode': isDarkMode }"
                      @click="expressInterest"
                    >
                      <span>INTERESTED IN THIS VEHICLE</span>
                      <v-icon right class="material-icons-round">favorite_border</v-icon>
                    </v-btn>
                  </div>
                </div>

                <!-- Vehicle Overview -->
                <div class="vehicle-overview-section mb-6">
                  <v-card class="overview-card" flat>
                    <v-card-title class="overview-title">
                      <h2>Vehicle Overview</h2>
                    </v-card-title>
                    <v-card-text class="overview-content">
                      <v-row>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.year">
                          <div class="overview-item">
                            <div class="overview-label">Year</div>
                            <div class="overview-value">{{ vehicle.year }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.make">
                          <div class="overview-item">
                            <div class="overview-label">Make</div>
                            <div class="overview-value">{{ vehicle.make }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.model">
                          <div class="overview-item">
                            <div class="overview-label">Model</div>
                            <div class="overview-value">{{ vehicle.model }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.trim">
                          <div class="overview-item">
                            <div class="overview-label">Trim</div>
                            <div class="overview-value">{{ vehicle.trim }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.body_style">
                          <div class="overview-item">
                            <div class="overview-label">Body Style</div>
                            <div class="overview-value">{{ vehicle.body_style }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.engine">
                          <div class="overview-item">
                            <div class="overview-label">Engine</div>
                            <div class="overview-value">{{ vehicle.engine }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.transmission">
                          <div class="overview-item">
                            <div class="overview-label">Transmission</div>
                            <div class="overview-value">{{ vehicle.transmission }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.fuel_type">
                          <div class="overview-item">
                            <div class="overview-label">Fuel Type</div>
                            <div class="overview-value">{{ vehicle.fuel_type }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.drivetrain">
                          <div class="overview-item">
                            <div class="overview-label">Drivetrain</div>
                            <div class="overview-value">{{ vehicle.drivetrain }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.exterior_color">
                          <div class="overview-item">
                            <div class="overview-label">Exterior Color</div>
                            <div class="overview-value">{{ vehicle.exterior_color }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.interior_color">
                          <div class="overview-item">
                            <div class="overview-label">Interior Color</div>
                            <div class="overview-value">{{ vehicle.interior_color }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6" sm="4" md="3" v-if="vehicle.doors">
                          <div class="overview-item">
                            <div class="overview-label">Doors</div>
                            <div class="overview-value">{{ vehicle.doors }}</div>
                          </div>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </div>
              </v-col>

              <!-- Right Column - Pricing & Contact -->
              <v-col cols="12" lg="4">

                <!-- Price Card -->
                <v-card class="price-card sticky-card" elevation="2">
                  <v-card-text class="price-content">
                    <div class="price-section">
                      <div class="price-main">{{ formatPrice(vehicle.amount) }}</div>
                      <div class="price-disclaimer">*Price excludes taxes, title, and fees</div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Key Vehicle Stats -->
                    <div class="key-stats mb-4">
                      <div class="stat-row" v-if="vehicle.odometer">
                        <span class="stat-label">Mileage</span>
                        <span class="stat-value">{{ formatMileage(vehicle.odometer) }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.exterior_color">
                        <span class="stat-label">Exterior</span>
                        <span class="stat-value">{{ vehicle.exterior_color }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.interior_color">
                        <span class="stat-label">Interior</span>
                        <span class="stat-value">{{ vehicle.interior_color }}</span>
                      </div>
                      <div class="stat-row" v-if="vehicle.fuel_type">
                        <span class="stat-label">Fuel Type</span>
                        <span class="stat-value">{{ vehicle.fuel_type }}</span>
                      </div>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Action Buttons -->
                    <div class="action-buttons">
                      <v-btn
                        color="green"
                        block
                        large
                        dark
                        class="mb-3 action-btn-primary"
                        @click="openContactDealerModal"
                      >
                        <v-icon left class="material-icons-round">phone</v-icon>
                        Contact Dealer
                      </v-btn>

                      <!-- <v-btn
                        color="green"
                        block
                        large
                        outlined
                        class="mb-3 action-btn-secondary"
                        @click="scheduleTestDrive"
                      >
                        Schedule Test Drive
                      </v-btn> -->

                      <v-btn
                        color="grey darken-1"
                        block
                        large
                        outlined
                        class="action-btn-secondary"
                        @click="saveVehicle"
                      >
                        Save Vehicle
                      </v-btn>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Vehicle Details Card -->
                <v-card class="details-card mt-4" elevation="1">
                  <v-card-title class="details-title">
                    <h3>Vehicle Details</h3>
                  </v-card-title>
                  <v-card-text class="details-content">
                    <div class="detail-list">
                      <div class="detail-row" v-if="vehicle.vin">
                        <span class="detail-label">VIN</span>
                        <span class="detail-value">{{ vehicle.vin }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.stock_number">
                        <span class="detail-label">Stock #</span>
                        <span class="detail-value">{{ vehicle.stock_number }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.transmission">
                        <span class="detail-label">Transmission</span>
                        <span class="detail-value">{{ vehicle.transmission }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.drivetrain">
                        <span class="detail-label">Drivetrain</span>
                        <span class="detail-value">{{ vehicle.drivetrain }}</span>
                      </div>
                      <div class="detail-row" v-if="vehicle.engine">
                        <span class="detail-label">Engine</span>
                        <span class="detail-value">{{ vehicle.engine }}</span>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </section>

      </div>

      <!-- Error State -->
      <div v-else-if="!loading" class="error-state" :class="{ 'dark-mode': isDarkMode }">
        <v-container>
          <div class="text-center py-12">
            <v-icon size="120" color="grey lighten-2">mdi-car-off</v-icon>
            <h2 class="mt-4 mb-2">Vehicle Not Found</h2>
            <p class="mb-4">The vehicle you're looking for could not be found.</p>
            <v-btn color="primary" @click="$router.push('/view-inventory')">
              <v-icon left class="material-icons-round">arrow_left</v-icon>
              Back to Inventory
            </v-btn>
          </div>
        </v-container>
      </div>
      <!-- Footer Section -->
      <section class="footer-section" :class="{ 'dark-mode': isDarkMode }">
        <v-container>
          <div class="footer-inner">
            <div class="footer-links">
              <span class="footer-link" @click="$router.push('/')">Inventory</span>
              <span class="footer-divider">|</span>
              <span class="footer-link" @click="$router.push('/reviews')">Testimonials</span>
              <span class="footer-divider">|</span>
              <span class="footer-link" @click="$router.push('/about-us')">About Us</span>
            </div>
            <p class="footer-copy">&copy; {{ new Date().getFullYear() }} Herrera Luxury Auto Sales. All rights reserved.</p>
          </div>
        </v-container>
      </section>

      <!-- Mobile Bottom Navigation -->
      <div class="mobile-bottom-nav" :class="{ 'dark-mode': isDarkMode }">
        <div class="bottom-nav-container">
          <div class="bottom-nav-item" @click="goToCard">
            <i class="material-icons-round bottom-nav-icon">contact_page</i>
            <span class="bottom-nav-label">Card</span>
          </div>
          <div class="bottom-nav-item" @click="showQRCode = true">
            <i class="material-icons-round bottom-nav-icon">qr_code_2</i>
            <span class="bottom-nav-label">Scan</span>
          </div>
          <div class="bottom-nav-item" @click="goToInventory">
            <i class="material-icons-round bottom-nav-icon">directions_car</i>
            <span class="bottom-nav-label">Inventory</span>
          </div>
          <div class="bottom-nav-item" @click="callDealer">
            <i class="material-icons-round bottom-nav-icon">call</i>
            <span class="bottom-nav-label">Call</span>
          </div>
        </div>
      </div>
    </v-main>

    <!-- Contact Dealer Modal -->
    <ContactDealerModal
      v-model="showContactDealerModal"
      :vehicle="vehicle"
    />

    <!-- QR Code Dialog -->
    <v-dialog v-model="showQRCode" width="300">
      <v-card class="text-center pa-4">
        <img v-if="company" :src="base_url_img + 'vcard/' + company.user_id + '-QR.png'" width="250" />
        <v-card-actions>
          <v-btn text block @click="showQRCode = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import ContactDealerModal from '@/components/ContactDealerModal.vue'

export default {
  name: 'VehicleDetailPage',
  components: {
    ContactDealerModal
  },
  data() {
    return {
      vehicle: null,
      loading: true,
      fallbackImage: require('@/assets/img/coming-soon.jpg'),
      brokenImages: new Set(),
      showContactDealerModal: false,
      isDarkMode: false,
      mobileMenuOpen: false,
      showQRCode: false,
      company: null,
      base_url_img: process.env.VUE_APP_IMG_URL || 'https://api.autosyncx.com/uploads/',
      expressingInterest: false
    }
  },
  computed: {
    breadcrumbItems() {
      return [
        {
          text: 'Home',
          disabled: false,
          href: '/'
        },
        {
          text: 'Inventory',
          disabled: false,
          href: '/view-inventory'
        },
        {
          text: this.vehicle ? this.getVehicleTitle() : 'Vehicle',
          disabled: true,
          href: '#'
        }
      ]
    }
  },
  mounted() {
    this.loadVehicle()
    this.loadDarkMode()
    this.loadCompany()

    // Listen for dark mode changes from header
    window.addEventListener('darkModeChanged', this.handleDarkModeChange)
  },
  beforeDestroy() {
    window.removeEventListener('darkModeChanged', this.handleDarkModeChange)
  },
  methods: {
    async loadVehicle() {
      try {
        const vehicleId = this.$route.params.id

        // Demo vehicles are stored in sessionStorage by CardInventory
        if (String(vehicleId).startsWith('demo-')) {
          const stored = sessionStorage.getItem('demo_vehicle_' + vehicleId)
          if (stored) {
            this.vehicle = JSON.parse(stored)
          } else {
            console.error('Demo vehicle not found in sessionStorage')
          }
          this.loading = false
          return
        }

        const domain = window.location.host

        const response = await axios.post(`/api/get-vehicle/${vehicleId}`, {
          domain: domain
        })

        if (response.data && response.data.data) {
          this.vehicle = response.data.data
        } else {
          console.error('Vehicle not found')
        }
      } catch (error) {
        console.error('Error loading vehicle:', error)
      } finally {
        this.loading = false
      }
    },

    getImageSrc(imageSrc) {
      if (!imageSrc || this.brokenImages.has(imageSrc)) {
        return this.fallbackImage
      }
      
      if (imageSrc.startsWith('http') || imageSrc.startsWith('data:image')) {
        return imageSrc
      }
      
      return this.fallbackImage
    },

    handleImageError(event) {
      const originalSrc = event.target.src
      if (originalSrc && !originalSrc.includes('coming-soon.jpg')) {
        this.brokenImages.add(originalSrc)
      }
      event.target.src = this.fallbackImage
    },

    getVehicleTitle() {
      if (!this.vehicle) return 'Vehicle'
      
      const parts = []
      if (this.vehicle.year) parts.push(this.vehicle.year)
      if (this.vehicle.make) parts.push(this.vehicle.make)
      if (this.vehicle.model) parts.push(this.vehicle.model)
      if (this.vehicle.trim) parts.push(this.vehicle.trim)
      
      return parts.join(' ') || 'Vehicle'
    },

    formatPrice(price) {
      if (!price) return 'Call for pricing'
      
      const numPrice = parseFloat(price)
      if (isNaN(numPrice) || numPrice <= 0) return 'Call for pricing'
      
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numPrice)
    },

    formatMileage(mileage) {
      if (!mileage) return 'N/A'

      const numMileage = parseInt(mileage)
      if (isNaN(numMileage)) return 'N/A'

      return new Intl.NumberFormat('en-US').format(numMileage) + ' mi'
    },

    contactDealer() {
      // Fallback phone call
      const phoneNumber = '6026101072'
      window.location.href = `tel:${phoneNumber}`
    },

    openContactDealerModal() {
      this.showContactDealerModal = true
    },

    scheduleTestDrive() {
      // TODO: Implement schedule test drive functionality
      console.log('Schedule test drive clicked')
    },

    saveVehicle() {
      // TODO: Implement save vehicle functionality
      console.log('Save vehicle clicked')
    },

    // Express interest in vehicle
    async expressInterest() {
      // Check if user is a lead (has inventory_lead_id in localStorage)
      const leadId = localStorage.getItem('inventory_lead_id')

      if (!leadId) {
        // User hasn't submitted lead form yet - open contact modal
        this.$toast?.info('Please submit your contact information first to express interest.', {
          position: 'top-right',
          duration: 5000
        })
        this.showContactDealerModal = true
        return
      }

      this.expressingInterest = true

      try {
        const response = await axios.post('/api/vehicle-interest/express-interest', {
          user_id: leadId,
          vehicle_id: this.vehicle.id,
          domain: window.location.hostname
        })

        if (response.data.flag === 1) {
          this.$toast?.success('Interest recorded! The dealer will contact you soon.', {
            position: 'top-right',
            duration: 5000
          })
        } else {
          this.$toast?.warning(response.data.message || 'Could not record interest', {
            position: 'top-right',
            duration: 5000
          })
        }
      } catch (error) {
        console.error('Error expressing interest:', error)
        this.$toast?.error('Failed to express interest. Please try again.', {
          position: 'top-right',
          duration: 5000
        })
      } finally {
        this.expressingInterest = false
      }
    },

    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
    },

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      localStorage.setItem('isDarkMode', this.isDarkMode);
      localStorage.setItem('herrera_theme', this.isDarkMode ? 'dark' : 'light');
      this.applyDarkMode();
      this.mobileMenuOpen = false;
      window.dispatchEvent(new CustomEvent('darkModeChanged', {
        detail: { isDarkMode: this.isDarkMode }
      }));
    },

    // Dark mode methods
    loadDarkMode() {
      const savedDarkMode = localStorage.getItem('isDarkMode')
      this.isDarkMode = savedDarkMode === 'true'
      this.applyDarkMode()
    },

    handleDarkModeChange(event) {
      this.isDarkMode = event.detail.isDarkMode
      this.applyDarkMode()
    },

    applyDarkMode() {
      if (this.isDarkMode) {
        document.body.classList.add('dark-mode')
      } else {
        document.body.classList.remove('dark-mode')
      }
    },

    // Load company data
    loadCompany() {
      try {
        const companyData = localStorage.getItem('company')
        if (companyData) {
          this.company = JSON.parse(companyData)
        }
      } catch (e) {
        console.error('Error loading company:', e)
      }
    },

    // Navigation methods
    goToCard() {
      this.$router.push('/')
    },

    goToInventory() {
      this.$router.push('/inventory')
    },

    callDealer() {
      const phone = this.company?.phone_m || this.company?.phone || ''
      if (phone) {
        window.location.href = `tel:${phone}`
      }
    }
  }
}
</script>

<style scoped>
/* =============================================
   Header
   ============================================= */
.autosyncx-header {
  width: 100%;
  position: fixed;
  top: 0;
  z-index: 200;
}

.header-nav {
  background: #ffffff;
  padding: 0 30px;
  min-height: 64px;
  border-bottom: 2px solid #e2e8f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  position: relative;
}

.header-nav-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  min-height: 64px;
}

.nav-tabs {
  display: flex;
  align-items: center;
}

.nav-tab {
  display: flex;
  align-items: center;
  height: 64px;
  padding: 0 18px;
  color: #1a202c;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  user-select: none;
}

.nav-tab:hover {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.05);
}

.nav-tab.active {
  color: #3d4a5c;
  border-bottom-color: #3d4a5c;
  background: rgba(61, 74, 92, 0.07);
}

.nav-tab-dropdown { position: relative; }

.nav-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.theme-toggle-btn {
  color: #718096 !important;
  transition: all 0.3s ease;
}

.theme-toggle-btn:hover {
  color: #3d4a5c !important;
  background: rgba(61, 74, 92, 0.08) !important;
}

.hamburger-menu {
  display: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.3s ease;
}

.hamburger-menu:hover { background: rgba(0, 0, 0, 0.06); }

.hamburger-icon {
  color: #2d3748 !important;
  font-size: 28px !important;
}

/* Cars For Sale Dropdown */
.cars-dropdown-menu {
  border-radius: 6px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12) !important;
}

.cars-dropdown-list .v-list-item {
  font-size: 0.8rem !important;
  font-weight: 700 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
  color: #1a202c !important;
}

.cars-dropdown-list .v-list-item:hover {
  background: rgba(61, 74, 92, 0.07) !important;
}

/* Mobile Menu Dropdown */
.mobile-menu-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  padding: 10px 0;
  z-index: 1000;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.mobile-menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
  color: #374151;
  cursor: pointer;
  transition: all 0.3s ease;
}

.mobile-menu-item:hover { background: rgba(61, 74, 92, 0.08); color: #3d4a5c; }

.menu-item-icon {
  color: #3d4a5c !important;
  font-size: 22px !important;
}

/* Dark Mode Header */
.dark-mode .header-nav {
  background: #1a1230 !important;
  border-bottom: 1px solid rgba(255,255,255,0.1) !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.4) !important;
}

.dark-mode .nav-tab { color: #e2e8f0 !important; }

.dark-mode .nav-tab:hover,
.dark-mode .nav-tab.active {
  color: #ffffff !important;
  border-bottom-color: #7c5cff !important;
  background: rgba(124, 92, 255, 0.1) !important;
}

.dark-mode .hamburger-icon { color: #ffffff !important; }

.dark-mode .theme-toggle-btn { color: #fbbf24 !important; }

.dark-mode .mobile-menu-dropdown { background: rgba(26, 18, 48, 0.98) !important; }

.dark-mode .mobile-menu-item { color: #ffffff !important; }

.dark-mode .mobile-menu-item:hover { background: rgba(124, 92, 255, 0.15) !important; }

.dark-mode .menu-item-icon { color: #a78bfa !important; }

.dark-mode .cars-dropdown-list .v-list-item { color: #ffffff !important; }

/* Responsive */
@media (max-width: 768px) {
  .header-nav { padding: 0 15px; position: relative; }
  .nav-tabs { display: none; }
  .hamburger-menu { display: flex; align-items: center; justify-content: center; }
}

/* Main Layout */
.vehicle-detail-container {
  background: #ffffff;
  min-height: 100vh;
}

/* Breadcrumb Section */
.breadcrumb-section {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.breadcrumb-item {
  font-size: 0.875rem;
  color: #6c757d;
}

.breadcrumb-item:not(.v-breadcrumbs-item--disabled) {
  color: #007bff;
}

/* Vehicle Content Section */
.vehicle-content-section {
  padding: 2rem 0;
  background: #ffffff;
  min-height: 100vh;
}

/* Vehicle Title Section */
.vehicle-title-section {
  margin-bottom: 2rem;
}

.vehicle-title {
  font-size: 2rem !important;
  font-weight: 700 !important;
  color: #212529 !important;
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.vehicle-subtitle {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  color: #6c757d;
  font-size: 0.875rem;
}

.subtitle-item {
  position: relative;
}

.subtitle-item:not(:last-child)::after {
  content: "•";
  margin-left: 1rem;
  color: #dee2e6;
}

/* Image Gallery Section */
.image-gallery-section {
  margin-bottom: 2rem;
}

.image-gallery-card {
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

.vehicle-carousel {
  border-radius: 12px;
}

.vehicle-image {
  object-fit: cover;
}

/* Interested Button Section */
.interested-button-section {
  width: 100%;
}

.interested-btn-main {
  height: 52px !important;
  border-radius: 8px !important;
  background: linear-gradient(135deg, #7c5cff 0%, #6366f1 100%) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 0.95rem !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  box-shadow: 0 4px 15px rgba(124, 92, 255, 0.3) !important;
  transition: all 0.3s ease;
}

.interested-btn-main:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(124, 92, 255, 0.4) !important;
}

.interested-btn-main .v-icon {
  color: #ffffff !important;
}

/* Dark Mode Interested Button */
.interested-btn-main.dark-mode {
  background: linear-gradient(135deg, #7c5cff 0%, #8b5cf6 100%) !important;
  box-shadow: 0 4px 15px rgba(124, 92, 255, 0.4) !important;
}

.interested-btn-main.dark-mode:hover {
  box-shadow: 0 6px 20px rgba(124, 92, 255, 0.5) !important;
}

/* Vehicle Overview Section */
.vehicle-overview-section {
  margin-bottom: 2rem;
}

.overview-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
}

.overview-title {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem !important;
}

.overview-title h2 {
  font-size: 1.25rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
  margin: 0;
}

.overview-content {
  padding: 1.5rem !important;
}

.overview-item {
  text-align: left;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  height: 100%;
}

.overview-label {
  font-size: 0.75rem;
  color: #6c757d;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
}

.overview-value {
  font-size: 0.875rem;
  color: #212529;
  font-weight: 600;
}

/* Price Card */
.price-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
  margin-bottom: 2rem;
}

.sticky-card {
  position: sticky;
  top: 140px;
  z-index: 10;
}

.price-content {
  padding: 2rem !important;
}

.price-section {
  text-align: center;
  margin-bottom: 1rem;
}

.price-main {
  font-size: 2.25rem;
  font-weight: 800;
  color: #28a745;
  margin-bottom: 0.5rem;
}

.price-disclaimer {
  font-size: 0.75rem;
  color: #6c757d;
  font-style: italic;
}

/* Key Stats */
.key-stats {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.stat-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
}

.stat-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.875rem;
}

.stat-value {
  font-weight: 600;
  color: #212529;
  font-size: 0.875rem;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.action-btn-primary {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-size: 1rem !important;
}

.action-btn-secondary {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  padding: 12px 24px !important;
  font-size: 1rem !important;
}

/* Details Card */
.details-card {
  border: 1px solid #e9ecef;
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05) !important;
  margin-top: 1.5rem;
}

.details-title {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 1.5rem !important;
}

.details-title h3 {
  font-size: 1.125rem !important;
  font-weight: 600 !important;
  color: #212529 !important;
  margin: 0;
}

.details-content {
  padding: 1.5rem !important;
}

.detail-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.detail-label {
  font-weight: 500;
  color: #6c757d;
  font-size: 0.875rem;
}

.detail-value {
  font-weight: 600;
  color: #212529;
  font-size: 0.875rem;
  text-align: right;
  word-break: break-word;
  max-width: 60%;
}

/* Error State */
.error-state {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .sticky-card {
    position: static;
    top: auto;
    z-index: auto;
  }
}

@media (max-width: 960px) {
  .vehicle-content-section {
    padding: 1.5rem 0;
  }

  .vehicle-title {
    font-size: 1.75rem !important;
  }

  .vehicle-subtitle {
    flex-direction: column;
    gap: 0.5rem;
  }

  .subtitle-item:not(:last-child)::after {
    display: none;
  }

  .image-gallery-card {
    border-radius: 8px !important;
  }

  .overview-card {
    border-radius: 8px !important;
  }

  .overview-title {
    padding: 1rem !important;
  }

  .overview-content {
    padding: 1rem !important;
  }

  .price-card {
    border-radius: 8px !important;
    margin-bottom: 1.5rem;
  }

  .price-content {
    padding: 1.5rem !important;
  }

  .price-main {
    font-size: 2rem;
  }

  .details-card {
    border-radius: 8px !important;
  }

  .details-title {
    padding: 1rem !important;
  }

  .details-content {
    padding: 1rem !important;
  }
}

@media (max-width: 600px) {
  .vehicle-content-section {
    padding: 1rem 0;
  }

  .vehicle-title {
    font-size: 1.5rem !important;
  }

  .vehicle-subtitle {
    font-size: 0.8rem;
  }

  .image-gallery-card {
    border-radius: 6px !important;
  }

  .overview-card {
    border-radius: 6px !important;
  }

  .overview-title {
    padding: 0.75rem !important;
  }

  .overview-content {
    padding: 0.75rem !important;
  }

  .overview-item {
    padding: 0.75rem;
  }

  .price-card {
    border-radius: 6px !important;
  }

  .price-content {
    padding: 1rem !important;
  }

  .price-main {
    font-size: 1.75rem;
  }

  .action-btn-primary,
  .action-btn-secondary {
    padding: 10px 20px !important;
    font-size: 0.9rem !important;
  }

  .details-card {
    border-radius: 6px !important;
  }

  .details-title {
    padding: 0.75rem !important;
  }

  .details-content {
    padding: 0.75rem !important;
  }

  .detail-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
  }

  .detail-value {
    text-align: left;
    max-width: 100%;
  }

  .breadcrumb-section {
    padding: 0.5rem 0;
  }

  .footer-section {
    padding-bottom: 80px;
  }
}

/* Footer Section */
.footer-section {
  background: #ffffff;
  border-top: 2px solid #e2e8f0;
  padding: 24px 0;
}

.footer-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer-link {
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #3d4a5c;
  cursor: pointer;
  transition: color 0.2s;
}

.footer-link:hover { color: #1a202c; }

.footer-divider {
  color: #cbd5e0;
  font-size: 0.9rem;
}

.footer-copy {
  font-size: 0.8rem;
  color: #718096;
  margin: 0;
}

/* Dark Mode Footer */
.footer-section.dark-mode {
  background: #1a1230;
  border-top: 1px solid #2d2d44;
}

.footer-section.dark-mode .footer-link { color: #a0aec0; }
.footer-section.dark-mode .footer-link:hover { color: #ffffff; }
.footer-section.dark-mode .footer-divider { color: #4a5568; }
.footer-section.dark-mode .footer-copy { color: #718096; }

/* =============================================
   Mobile Bottom Navigation
   ============================================= */
.mobile-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: 70px;
  z-index: 1000;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 100%);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.mobile-bottom-nav.dark-mode {
  background: linear-gradient(180deg, rgba(30, 30, 46, 0.98) 0%, rgba(30, 30, 46, 0.95) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.3);
}

.bottom-nav-container {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 100%;
  max-width: 500px;
  margin: 0 auto;
  padding: 0 10px;
}

.bottom-nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 8px 16px;
  border-radius: 16px;
  min-width: 70px;
  position: relative;
}

.bottom-nav-item:hover {
  background: rgba(59, 130, 246, 0.1);
  transform: translateY(-2px);
}

.bottom-nav-item:active {
  transform: translateY(0);
}

.bottom-nav-item.active {
  background: rgba(59, 130, 246, 0.15);
}

.bottom-nav-item.active .bottom-nav-icon {
  color: #3b82f6;
}

.bottom-nav-item.active .bottom-nav-label {
  color: #3b82f6;
  font-weight: 600;
}

.bottom-nav-icon {
  font-size: 24px;
  color: #64748b;
  margin-bottom: 4px;
  transition: all 0.3s ease;
}

.bottom-nav-label {
  font-size: 11px;
  font-weight: 500;
  color: #64748b;
  letter-spacing: 0.3px;
}

/* Dark mode bottom nav */
.mobile-bottom-nav.dark-mode .bottom-nav-icon {
  color: #a0aec0;
}

.mobile-bottom-nav.dark-mode .bottom-nav-label {
  color: #a0aec0;
}

.mobile-bottom-nav.dark-mode .bottom-nav-item:hover {
  background: rgba(124, 92, 255, 0.15);
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active {
  background: rgba(124, 92, 255, 0.2);
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active .bottom-nav-icon {
  color: #a78bfa;
}

.mobile-bottom-nav.dark-mode .bottom-nav-item.active .bottom-nav-label {
  color: #a78bfa;
}

/* Show bottom nav only on mobile */
@media (max-width: 768px) {
  .mobile-bottom-nav {
    display: block;
  }
}

/* Safe area for iOS devices */
@supports (padding-bottom: env(safe-area-inset-bottom)) {
  .mobile-bottom-nav {
    padding-bottom: env(safe-area-inset-bottom);
    height: calc(70px + env(safe-area-inset-bottom));
  }
}

/* =============================================
   Dark Mode Styles
   ============================================= */
.vehicle-detail-container.dark-mode {
  background: #1a1230 !important;
}

.vehicle-detail-container.dark-mode .vehicle-content-section {
  background: #1a1230 !important;
}

.vehicle-detail-container.dark-mode .vehicle-title {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .vehicle-subtitle {
  color: #a0aec0 !important;
}

.vehicle-detail-container.dark-mode .subtitle-item {
  color: #a0aec0 !important;
}

.vehicle-detail-container.dark-mode .subtitle-item:not(:last-child)::after {
  color: #4a5568;
}

/* Dark Mode - Breadcrumb */
.vehicle-detail-container.dark-mode .breadcrumb-section {
  background: #241b3d !important;
  border-bottom: 1px solid #2d2d44;
}

.vehicle-detail-container.dark-mode .breadcrumb-item {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .breadcrumb-item.v-breadcrumbs-item--disabled {
  color: #e0e0e0 !important;
}

.vehicle-detail-container.dark-mode .v-breadcrumbs .v-icon {
  color: #ffffff !important;
}

/* Deep selectors for Vuetify breadcrumb components in dark mode */
.vehicle-detail-container.dark-mode ::v-deep .v-breadcrumbs__item {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode ::v-deep .v-breadcrumbs__item--disabled {
  color: #e0e0e0 !important;
}

.vehicle-detail-container.dark-mode ::v-deep .v-breadcrumbs__divider {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode ::v-deep .v-breadcrumbs a {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .breadcrumb-section ::v-deep * {
  color: #ffffff !important;
}

/* Dark Mode - Image Gallery */
.vehicle-detail-container.dark-mode .image-gallery-card {
  background: #241b3d !important;
  border-color: #2d2d44 !important;
}

/* Dark Mode - Overview Card */
.vehicle-detail-container.dark-mode .overview-card {
  background: #241b3d !important;
  border-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .overview-title {
  background: #1e1535 !important;
  border-bottom-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .overview-title h2 {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .overview-item {
  background: #1e1535 !important;
  border-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .overview-label {
  color: #8b8b9e !important;
}

.vehicle-detail-container.dark-mode .overview-value {
  color: #ffffff !important;
}

/* Dark Mode - Price Card */
.vehicle-detail-container.dark-mode .price-card {
  background: #241b3d !important;
  border-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .price-main {
  color: #4ade80 !important;
}

.vehicle-detail-container.dark-mode .price-disclaimer {
  color: #8b8b9e !important;
}

.vehicle-detail-container.dark-mode .stat-label {
  color: #8b8b9e !important;
}

.vehicle-detail-container.dark-mode .stat-value {
  color: #ffffff !important;
}

/* Dark Mode - Action Buttons */
.vehicle-detail-container.dark-mode .action-btn-primary {
  background: #7c5cff !important;
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .action-btn-secondary {
  border-color: #7c5cff !important;
  color: #a78bfa !important;
}

/* Dark Mode - Details Card */
.vehicle-detail-container.dark-mode .details-card {
  background: #241b3d !important;
  border-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .details-title {
  background: #1e1535 !important;
  border-bottom-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .details-title h3 {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .detail-row {
  background: #1e1535 !important;
  border-color: #2d2d44 !important;
}

.vehicle-detail-container.dark-mode .detail-label {
  color: #8b8b9e !important;
}

.vehicle-detail-container.dark-mode .detail-value {
  color: #ffffff !important;
}

/* Dark Mode - Error State */
.vehicle-detail-container.dark-mode .error-state {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .error-state h2 {
  color: #ffffff !important;
}

.vehicle-detail-container.dark-mode .error-state p {
  color: #a0aec0 !important;
}
</style>
